import { useState, useMemo, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Person, AccessTime, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ICalendarEvent } from "../Calendar/CalendarUtils";
import WeeklyCalendar from "../Calendar/WeeklyCalendar";
import { getData } from "../../Services/getData";
import { updateData } from "../../Services/updateData";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import {
  DrivingHoursOrderSummary,
  SendNotificationPayload,
} from "../../Utils/Types";
import { DRIVING_LESSON_NOTIFICATION_TYPES } from "../../Utils/Constants";
import {
  renderStatusChip,
  transformHoursToEvents,
  generateTimeSlots,
  formatTimeDisplay,
  hasBookingOnDate,
  WeekNavigationHeader,
} from "./drivingSessionHelpers";
import styles from "./DrivingSessionScheduling.module.css";
import calendarStyles from "../Calendar/WeeklyCalendar.module.css";
import genericStyles from "../../Styles/genericStyles.module.css";
import moment from "moment";
import { HourStatusEnum } from "../../Utils/Enums";
import BookingConfirmationModal from "./BookingConfirmationModal";
import Config from "../../Utils/Config";
import GenericModal from "../GenericModal";

export default function DrivingSessionScheduling() {
  const [currentWeekStart, setCurrentWeekStart] = useState<moment.Moment>(
    moment().startOf("isoWeek")
  );
  const [hours, setHours] = useState<any[]>([]);
  const [orderSummary, setOrderSummary] = useState<DrivingHoursOrderSummary[]>(
    []
  );
  const [modalState, setModalState] = useState<{
    open: boolean;
    date: Date | null;
    event: ICalendarEvent | null;
  }>({
    open: false,
    date: null,
    event: null,
  });

  const [deleteModalState, setDeleteModalState] = useState<{
    open: boolean;
    event: ICalendarEvent | null;
  }>({
    open: false,
    event: null,
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Fetch hours when week changes
  useEffect(() => {
    fetchHours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart]);

  // Fetch order summary on mount
  useEffect(() => {
    fetchOrderSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHours = async () => {
    const clientId = localStorage.getItem("clientId");

    const url = `${endpoints.hours.byClientInstructors}/${clientId}`;

    const response = await getData(url);
    if (response?.data) {
      setHours(response.data);
    }
  };

  const fetchOrderSummary = async () => {
    const clientId = localStorage.getItem("clientId");

    const url = `${endpoints.hours.orderSummary}/${clientId}`;

    const response = await getData(url);
    if (response?.data) {
      setOrderSummary(response.data);
    }
  };

  const sendLessonNotification = async (
    hourId: string,
    notificationType: string
  ) => {
    const clientId = localStorage.getItem("clientId");
    const hourData = hours.find((h: any) => h._id === hourId);

    if (!hourData?.instructorId || !clientId) {
      return;
    }

    const notificationPayload: SendNotificationPayload = {
      type: notificationType,
      clientId,
      instructorId: hourData.instructorId._id,
      hourId,
    };

    await postData(
      endpoints.notifications.sendDrivingLesson,
      notificationPayload
    );
  };

  // Check if client has any order with payment less than minimum required
  const hasUnderpaidOrder = useMemo(() => {
    return orderSummary.some(
      (order) => order.totalPaid < Config.minPaymentForDrivingLessons
    );
  }, [orderSummary]);

  const timeSlots = useMemo(() => generateTimeSlots(), []);
  const events: ICalendarEvent[] = useMemo(
    () => transformHoursToEvents(hours),
    [hours]
  );

  const handleDateSelect = (date: Date) => {
    // Block calendar interaction if user has underpaid orders
    if (hasUnderpaidOrder) {
      return;
    }

    setModalState({
      open: true,
      date,
      event: null,
    });
  };

  const handleEventClick = (event: ICalendarEvent) => {
    // Block calendar interaction if user has underpaid orders
    if (hasUnderpaidOrder) {
      return;
    }

    if (event.clientId) {
      // If the event is already booked, do nothing
      return;
    }

    // Check if client has remaining hours - prevent booking
    const hasNoRemainingHours = orderSummary.every(
      (order) => order.remainingHours <= 0
    );
    if (hasNoRemainingHours && orderSummary.length > 0) {
      return; // Don't allow booking, notice already visible at top
    }

    // Check if client already has a booking on this day
    const clickedDate = moment(event.date).format("YYYY-MM-DD");
    const clientId = localStorage.getItem("clientId");

    if (hasBookingOnDate(hours, clickedDate, clientId!) && event.available) {
      // Show error - client already has a booking on this day
      toast.error(Vocabulary.alreadyBookedForDay);
      return;
    }

    setModalState({
      open: true,
      date: new Date(event.date),
      event,
    });
  };

  const handleCloseModal = () => {
    setModalState({
      open: false,
      date: null,
      event: null,
    });
  };

  const handleConfirmBooking = async (comments: string) => {
    if (!modalState.event) {
      return;
    }

    const clientId = localStorage.getItem("clientId");
    const hourId = modalState.event.id;
    const updatePayload = {
      status: HourStatusEnum.PENDING,
      comments,
      clientId,
      orderId: modalState.event.orderId || null,
    };

    // Update the hour with comments first
    await updateData(`${endpoints.hours.main}/${hourId}`, updatePayload);

    // Then send notification
    await sendLessonNotification(
      hourId,
      DRIVING_LESSON_NOTIFICATION_TYPES.LESSON_BOOKING_REQUEST
    );

    // Finally refresh the data
    await fetchHours();
    handleCloseModal();
  };

  const handleDeleteClick = (
    event: ICalendarEvent,
    eventClickEvent: React.MouseEvent
  ) => {
    eventClickEvent.stopPropagation();

    if (!canDeleteEvent(event)) {
      return;
    }

    setDeleteModalState({ open: true, event });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ open: false, event: null });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalState.event) {
      return;
    }

    const hourId = deleteModalState.event.id;

    // Update the record to remove clientId and set status back to AVAILABLE
    const updatePayload = {
      clientId: null,
      status: HourStatusEnum.AVAILABLE,
      comments: null,
      orderId: null,
    };

    const response = await updateData(
      `${endpoints.hours.main}/${hourId}`,
      updatePayload
    );

    if (response) {
      await sendLessonNotification(
        hourId,
        DRIVING_LESSON_NOTIFICATION_TYPES.LESSON_CANCELED_BY_CLIENT
      );
      await fetchHours();
    }

    setDeleteModalState({ open: false, event: null });
  };

  const canDeleteEvent = (event: ICalendarEvent): boolean => {
    const clientId = localStorage.getItem("clientId");
    const hourData = hours.find((h: any) => h._id === event.id);

    // Must be user's own booking
    if (!hourData || hourData.clientId !== clientId) {
      return false;
    }

    // Cannot delete available slots (only booked or approved)
    if (hourData.status === HourStatusEnum.AVAILABLE) {
      return false;
    }

    // Check time requirement
    const eventStart = moment(event.startDate);
    const now = moment();
    const hoursUntilEvent = eventStart.diff(now, "hours", true);

    return hoursUntilEvent >= Config.minHoursBeforeEventDeletion;
  };

  const renderEventCard = (event: ICalendarEvent) => {
    const isAvailable = event.available;
    const isBooked = event.booked;
    const isApproved = event.approved;
    const showDeleteButton = event.isOwnBooking && canDeleteEvent(event);

    // Format time display
    const timeDisplay = formatTimeDisplay(
      event.startDate || "",
      event.endDate || ""
    );

    return (
      <Box
        className={`${styles.eventCardContent} ${
          isApproved
            ? styles.approvedEvent
            : isBooked
            ? styles.bookedEvent
            : isAvailable
            ? styles.availableEvent
            : styles.unavailableEvent
        }`}
      >
        {/* Delete Button */}
        {showDeleteButton && (
          <Box className={calendarStyles.eventActions}>
            <Tooltip title={Vocabulary.delete}>
              <IconButton
                className={calendarStyles.deleteButton}
                onClick={(e) => handleDeleteClick(event, e)}
                size="small"
              >
                <Delete sx={{ fontSize: "16px" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Status Badge */}
        <Box className={styles.eventCardHeader}>
          {renderStatusChip(isAvailable, isBooked, isApproved)}
        </Box>

        {/* Time Display */}
        {timeDisplay && (
          <Box className={styles.eventCardRow}>
            <AccessTime sx={{ fontSize: "14px" }} />
            <Typography variant="caption" className={styles.eventCardText}>
              {timeDisplay}
            </Typography>
          </Box>
        )}

        {/* Instructor Name */}
        {event.instructorName && (
          <Box className={styles.eventCardRow}>
            <Person sx={{ fontSize: "14px" }} />
            <Typography variant="caption" className={styles.eventCardText}>
              {event.instructorName}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      className={`${genericStyles.scrollableContainer} ${styles.pageContainer}`}
    >
      <Typography variant="h4" className={styles.pageTitle}>
        {Vocabulary.drivingSessionScheduling}
      </Typography>

      <Paper className={styles.contentPaper}>
        <WeekNavigationHeader
          currentWeekStart={currentWeekStart}
          setCurrentWeekStart={setCurrentWeekStart}
        />

        {/* Notices and Info */}
        {hasUnderpaidOrder ? (
          <Box className={styles.noticeContainer}>
            {orderSummary
              .filter((order) => order.totalPaid < Config.minPaymentForDrivingLessons)
              .map((order, index, filteredOrders) => (
                <Alert
                  key={index}
                  severity="error"
                  className={styles.noticeBox}
                  sx={{ mb: index < filteredOrders.length - 1 ? 2 : 0 }}
                >
                  <Typography className={styles.noticeText}>
                    {Vocabulary.minPaymentRequiredForDrivingLessons.replace(
                      "{amount}",
                      Config.minPaymentForDrivingLessons.toString()
                    )}
                  </Typography>
                </Alert>
              ))}
          </Box>
        ) : orderSummary.some((order) => order.remainingHours <= 0) ? (
          <Box className={styles.noticeContainer}>
            {orderSummary
              .filter((order) => order.remainingHours <= 0)
              .map((order, index, filteredOrders) => (
                <Alert
                  key={index}
                  severity="warning"
                  className={styles.noticeBox}
                  sx={{ mb: index < filteredOrders.length - 1 ? 2 : 0 }}
                >
                  <Typography className={styles.noticeText}>
                    {Vocabulary.allHoursCompletedForService.replace(
                      "{productName}",
                      order.productName
                    )}
                  </Typography>
                </Alert>
              ))}
          </Box>
        ) : (
          <Alert severity="info" className={styles.infoAlert}>
            {Vocabulary.selectAvailableSlotInfo}
          </Alert>
        )}

        {/* Weekly Calendar */}
        <Box
          className={styles.calendarWrapper}
          sx={{
            opacity: hasUnderpaidOrder ? 0.5 : 1,
            pointerEvents: hasUnderpaidOrder ? 'none' : 'auto',
            position: 'relative'
          }}
        >
          <WeeklyCalendar
            events={events}
            onDateSelect={handleDateSelect}
            onEventClick={handleEventClick}
            currentDate={currentWeekStart.toDate()}
            timeSlots={timeSlots}
            renderEventCard={renderEventCard}
          />
        </Box>
      </Paper>

      {/* Booking Confirmation Modal */}
      <BookingConfirmationModal
        open={modalState.open}
        date={modalState.date}
        event={modalState.event}
        onClose={handleCloseModal}
        onConfirm={handleConfirmBooking}
      />

      {/* Delete Confirmation Modal */}
      <GenericModal
        open={deleteModalState.open}
        onClose={handleDeleteCancel}
        title={Vocabulary.confirmDeleteTitle}
        actions={
          <>
            <Button
              onClick={handleDeleteCancel}
              color="primary"
              variant="outlined"
            >
              {Vocabulary.no}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
            >
              {Vocabulary.yes}
            </Button>
          </>
        }
      >
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", textAlign: "center" }}
        >
          {Vocabulary.confirmDeleteMessage}
        </Typography>
      </GenericModal>
    </Box>
  );
}
